import Nav from './Nav';
import ReactMarkdown from 'react-markdown';
import React, { useState, useEffect } from 'react';
import ToolHistory from './ToolHistory';


const mdComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2" style={{ color: '#1B3A5C' }}>{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold mt-4 mb-2" style={{ color: '#1B3A5C' }}>{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-1" style={{ color: '#1B3A5C' }}>{children}</h3>,
  p:  ({ children }) => <p className="mb-3 leading-relaxed text-gray-800">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-800">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-gray-800">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 pl-4 italic my-3 text-gray-600" style={{ borderColor: '#C8921A' }}>
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-gray-300" />,
};

export default function EvangelismTool({ onBack, token, onAuthExpired, onNavigate, onLogout, userRole, currentPage, userStatus, onUpgradeNeeded, onGenerationComplete }) {
  const [form, setForm] = useState({ context: 'Workplace', listenerBackground: 'No Church Background' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [genCount, setGenCount] = useState(0);
  // Show upgrade modal immediately if user is at limit
  useEffect(() => {
    if (userStatus && userStatus.role === 'free' && userStatus.generations_remaining === 0) {
      if (onUpgradeNeeded) onUpgradeNeeded();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');
    setError('');
    try {
      const res = await fetch('/api/evangelism', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.status === 401) {
        if (onAuthExpired) onAuthExpired();
        return;
      }
      if (res.status === 429) {
        if (onUpgradeNeeded) onUpgradeNeeded();
        return;
      }
      if (!res.ok) {
        setError(data.error || 'Failed to generate. Please try again.');
      } else {
        setResult(data.result || '');
        setGenCount(c => c + 1);
        if (onGenerationComplete) onGenerationComplete();
      }
    } catch (err) {
      setError('Error connecting to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Nav onNavigate={onNavigate} onLogout={onLogout} userRole={userRole} currentPage={currentPage} />
      <main className="flex-1 px-6 py-10 max-w-2xl mx-auto w-full">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button onClick={onBack} className="hover:text-gray-700">Dashboard</button>
          <span>›</span>
          <span className="text-gray-700">Evangelism Guide</span>
        </div>
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#1B3A5C' }}>Evangelism Conversation Guide</h2>

        <form onSubmit={handleSubmit} className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Context</label>
            <select
              value={form.context}
              onChange={(e) => setForm({ ...form, context: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none"
            >
              <option>Workplace</option>
              <option>Neighborhood</option>
              <option>Family Member</option>
              <option>College Campus</option>
              <option>Coffee Shop</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Listener Background</label>
            <select
              value={form.listenerBackground}
              onChange={(e) => setForm({ ...form, listenerBackground: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none"
            >
              <option>No Church Background</option>
              <option>Cultural Christian</option>
              <option>Other Religion</option>
              <option>Skeptic/Agnostic</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: '#C8921A' }}
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            )}
            {loading ? 'Generating...' : 'Generate Conversation Guide'}
          </button>
        </form>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {result && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-gray-700">Your Evangelism Guide</h3>
              <button onClick={handleCopy} className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50">
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
            <div className="rounded-lg p-5" style={{ backgroundColor: '#F5F5F5' }}>
              <ReactMarkdown components={mdComponents}>{result}</ReactMarkdown>
            </div>
          </div>
        )}
        <ToolHistory
          token={token}
          toolName="evangelism"
          displayName="Evangelism Guide"
          onAuthExpired={onAuthExpired}
          newGenerationCount={genCount}
        />
      </main>
    </div>
  );
}
